import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { TopNav } from "@/components/TopNav";
import { SideNav } from "@/components/SideNav";
import { ConfirmProvider, ToastProvider } from "@/components/ui";
import { SmartListProvider } from "@/features/lists/SmartListContext";
import { api } from "@/lib/api";
import type { WorkspaceSettings } from "@/lib/types";
import { cn } from "@/lib/utils";
import { FrontDeskChatButton } from "@/features/teamchat/FrontDeskChat";
import { LG, useMediaQuery } from "@/lib/media";
import { isPracticeStaff, useMe, workspaceNameFromMe } from "@/lib/me";

/**
 * Pages that want to fill the full viewport width edge-to-edge (no max-width
 * cap, no horizontal padding) — listed by route prefix. Everything else gets
 * a comfortable centered container with breathing room.
 *
 * Contact-detail (`/contacts/:id`) is also full-bleed because its three
 * columns each manage their own scroll and the page must fit the viewport
 * exactly — pattern-matched separately so /contacts (the list) keeps the
 * standard padded layout.
 */
const FULL_BLEED_ROUTES = ["/inbox", "/pipelines", "/calendar", "/mail", "/schedule"];
const CONTACT_DETAIL = /^\/contacts\/[^/]+$/;
// Automation detail owns its own scrollable layout so the flow diagram
// can fill the rest of the viewport. The list view (`/automations`)
// keeps the standard padded layout.
const AUTOMATION_DETAIL = /^\/automations\/[^/]+$/;

export function CrmLayout() {
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);
  // The floating button needs to be OUTSIDE the top bar: that header carries
  // backdrop-blur, which makes it the containing block for anything fixed
  // inside it, so a fixed button in there pins to the header, not the window.
  const desktop = useMediaQuery(LG);
  const me = useMe();
  const { pathname } = useLocation();
  const fullBleed = FULL_BLEED_ROUTES.some((p) => pathname === p || pathname.startsWith(p + "/"))
    || CONTACT_DETAIL.test(pathname)
    || AUTOMATION_DETAIL.test(pathname);

  // Settings is practice staff only - it holds her alert numbers and the
  // payment setup - so a login narrow enough to be kept out of it (somebody
  // brought in to run the ads) takes the practice's name off /staff/me
  // instead, rather than asking for a page it will be refused.
  const staff = isPracticeStaff(me);
  useEffect(() => {
    if (me && !staff) { setWorkspaceName(workspaceNameFromMe()); return; }
    if (!me) return;
    api.get<{ settings: WorkspaceSettings }>("/settings")
      .then((d) => setWorkspaceName(d.settings.workspace_name || "CRM"))
      .catch(() => setWorkspaceName(workspaceNameFromMe() ?? "CRM"));
  }, [me, staff]);

  return (
    <ToastProvider>
      <ConfirmProvider>
      <SmartListProvider>
        {/* h-screen + flex-col + min-h-0 on <main> so full-bleed pages can
            use `h-full` to fill exactly the remaining viewport without
            ever computing 100vh − topnav heights manually (those calcs
            kept drifting by a few pixels because of border lines between
            the primary and secondary nav rows). Padded pages still grow
            naturally inside the same flex slot. */}
        {/* lg+: SideNav column + content column (TopNav is a slim toolbar).
            Below lg: SideNav hides and TopNav is the full navigation. */}
        <div className="flex h-screen overflow-hidden bg-zinc-950">
          <SideNav workspaceName={workspaceName ?? workspaceNameFromMe() ?? undefined} />
          <div className="flex min-w-0 flex-1 flex-col">
            <TopNav workspaceName={workspaceName ?? workspaceNameFromMe() ?? undefined} />
            <main className={cn("flex-1 min-h-0", fullBleed ? "" : "overflow-y-auto")}>
              {fullBleed ? (
                // Full-bleed: page owns its own layout (sidebars, scroll, etc.).
                <Outlet />
              ) : (
                /* "the front desk chat button is in my way to fix packages
                    lol that damm button" - the chat button floats over the
                    bottom-right corner of every page, which is exactly where
                    a list's own row buttons end up once the page scrolls. A
                    modal already covers it (z-50 over z-40); a long list did
                    not. So every padded page keeps a button's worth of room
                    below its content, and only when the button is actually
                    there. */
                <div className={cn(
                  "mx-auto w-full max-w-screen-2xl px-3 py-5 sm:px-4 sm:py-6 lg:px-6",
                  desktop && staff && "pb-24 sm:pb-24",
                )}>
                  <Outlet />
                </div>
              )}
            </main>
          </div>
          {/* Same corner of every page — Front desk, Calendar, Messages and
              the rest. On a phone it is a chip in the top bar instead. */}
          {desktop && staff && <FrontDeskChatButton variant="fab" />}
        </div>
      </SmartListProvider>
      </ConfirmProvider>
    </ToastProvider>
  );
}
